import { createClient } from '@connectrpc/connect';
import { create } from '@bufbuild/protobuf';
import {
  BranchKind,
  CreateBranchRequestSchema,
  ListCommitsRequestSchema,
  VersionControlService,
  type BranchInfo,
  type CommitInfo,
} from '@puppycad/proto';
import { grpcWebTransport } from '@/services/api/transport/grpc-web-transport';
import type {
  FetchProjectCommitRecordsOptions,
  FetchProjectCommitSnapshotOptions,
  ProjectCommitRecord,
  ProjectCommitSnapshot,
  VersionWorkspaceCreationResult,
  VersionWorkspaceService,
} from '../types/version-workspace-types';

const DEFAULT_COMMIT_RECORD_PAGE_SIZE = 500;

const versionClient = createClient(VersionControlService, grpcWebTransport);

export interface VersionWorkspaceRepositoryContext {
  /** 当前编辑器已打开项目对应的仓库 ID。 */
  readonly repositoryId: bigint;
  /** 当前编辑器已打开项目对应的分支 ID；缺省时由后端返回全部分支类型的提交。 */
  readonly branchId?: bigint;
  /** 当前编辑器已打开项目对应的提交 ID，用于后端未返回 headCommitId 时兜底标记当前版本。 */
  readonly currentCommitId?: string;
}

export interface CreateVersionWorkspaceServiceOptions {
  /** 读取当前编辑器的仓库、分支与提交上下文。 */
  readonly getRepositoryContext: () => VersionWorkspaceRepositoryContext | null;
  /** 每次拉取提交记录的数量。 */
  readonly pageSize?: number;
}

interface CommitRecordsCache {
  /** 缓存所属的项目与仓库上下文 key。 */
  readonly key: string;
  /** 当前上下文下的提交记录。 */
  readonly records: readonly ProjectCommitRecord[];
}

interface PendingCommitRecordsRequest {
  /** 正在加载的提交记录上下文 key。 */
  readonly key: string;
  /** 发起该请求时使用的取消信号；不同 signal 代表不同生命周期 owner，不能互相复用。 */
  readonly signal?: AbortSignal;
  /** 当前上下文对应的提交记录加载任务。 */
  readonly promise: Promise<readonly ProjectCommitRecord[]>;
}

/**
 * 将 protobuf Timestamp 转成页面消费的 ISO 字符串。
 *
 * @param timestamp 后端返回的 protobuf 时间。
 * @returns ISO 时间字符串；缺失或非法时返回空字符串。
 */
function timestampToIsoString(timestamp: CommitInfo['createdAt']): string {
  if (!timestamp) {
    return '';
  }

  const milliseconds = Number(timestamp.seconds) * 1000 + Math.floor(timestamp.nanos / 1_000_000);
  const date = new Date(milliseconds);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
}

/**
 * 归一化编辑器传入的仓库上下文。
 *
 * @param context 调用方提供的仓库上下文。
 * @returns 可用于真实版本接口的上下文；仓库 ID 缺失时返回 null。
 */
function normalizeRepositoryContext(
  context: VersionWorkspaceRepositoryContext | null,
): VersionWorkspaceRepositoryContext | null {
  if (!context || context.repositoryId <= 0n) {
    return null;
  }

  return {
    repositoryId: context.repositoryId,
    ...(context.branchId !== undefined && context.branchId > 0n
      ? { branchId: context.branchId }
      : {}),
    ...(context.currentCommitId?.trim()
      ? { currentCommitId: context.currentCommitId.trim() }
      : {}),
  };
}

/**
 * 为当前提交记录查询上下文生成缓存 key。
 *
 * @param projectId 当前项目 ID。
 * @param context 当前仓库上下文。
 * @returns 稳定缓存 key。
 */
function createCommitRecordsContextKey(
  projectId: string,
  context: VersionWorkspaceRepositoryContext,
): string {
  return [
    projectId.trim(),
    context.repositoryId.toString(),
    context.branchId?.toString() ?? 'default-branch-kind',
    context.currentCommitId?.trim() ?? '',
  ].join(':');
}

/**
 * 解析当前提交记录列表应该使用的分支信息。
 *
 * @param branches ListCommits 返回的分支信息。
 * @param context 当前仓库上下文。
 * @returns 与当前查询最匹配的分支信息。
 */
function resolveActiveBranch(
  branches: readonly BranchInfo[],
  context: VersionWorkspaceRepositoryContext,
): BranchInfo | undefined {
  if (context.branchId !== undefined) {
    const matchedBranch = branches.find((branch) => branch.branchId === context.branchId);
    if (matchedBranch) {
      return matchedBranch;
    }
  }

  return branches[0];
}

/**
 * 解析提交记录模型需要的分支 ID。
 *
 * @param branch 当前分支信息。
 * @param context 当前仓库上下文。
 * @returns 用于历史图稳定分轨的分支 ID 字符串。
 */
function resolveCommitRecordBranchId(
  branch: BranchInfo | undefined,
  context: VersionWorkspaceRepositoryContext,
): string {
  return (branch?.branchId ?? context.branchId ?? 0n).toString();
}

/**
 * 解析提交记录展示用分支名称。
 *
 * @param branch 当前分支信息。
 * @param context 当前仓库上下文。
 * @returns 可展示的分支名称。
 */
function resolveCommitRecordBranchName(
  branch: BranchInfo | undefined,
  context: VersionWorkspaceRepositoryContext,
): string {
  const branchName = branch?.branchName.trim() ?? '';
  if (branchName) {
    return branchName;
  }

  return context.branchId !== undefined ? context.branchId.toString() : '默认分支';
}

/**
 * 解析当前可编辑工作区对应的提交 ID。
 *
 * @param branch 当前分支信息。
 * @param context 当前仓库上下文。
 * @returns 可用于标记当前工作区提交的提交 ID。
 */
function resolveCurrentWorkspaceCommitId(
  branch: BranchInfo | undefined,
  context: VersionWorkspaceRepositoryContext,
): string {
  const openedCommitId = context.currentCommitId?.trim() ?? '';
  if (openedCommitId) {
    // OpenProject 返回的提交 ID 才是编辑器真实加载的基线；分支 head 可能已被后续提交推进。
    return openedCommitId;
  }

  return branch?.headCommitId.trim() ?? '';
}

/**
 * 将后端 CommitInfo 映射为编辑器提交记录模型。
 *
 * @param commit 后端提交信息。
 * @param branch 当前分支信息。
 * @param context 当前仓库上下文。
 * @returns 编辑器提交记录。
 */
function toProjectCommitRecord(
  commit: CommitInfo,
  branch: BranchInfo | undefined,
  context: VersionWorkspaceRepositoryContext,
): ProjectCommitRecord {
  const message = commit.message.trim();
  const currentWorkspaceCommitId = resolveCurrentWorkspaceCommitId(branch, context);

  return {
    id: commit.commitId,
    message: message || commit.commitId,
    authorName: commit.createdBy.trim(),
    committedAt: timestampToIsoString(commit.createdAt),
    branchId: resolveCommitRecordBranchId(branch, context),
    branchName: resolveCommitRecordBranchName(branch, context),
    parentIds: [...commit.parentCommitIds],
    isCurrentWorkspaceCommit: Boolean(currentWorkspaceCommitId)
      && commit.commitId === currentWorkspaceCommitId,
  };
}

/**
 * 创建版本工作区真实接口服务。
 *
 * 设计说明：
 * 1. 提交记录不传 `branchKind`，由后端按默认全部分支类型处理；
 * 2. 历史预览只生成根节点快照定位信息，实际模型读取统一由整项目切换函数完成；
 * 3. 创建工作区分支通过 CreateBranch 写入后端，并固定使用 BranchKind.WORKSPACE。
 *
 * @param options 创建选项。
 * @returns 版本工作区服务接口。
 */
export function createVersionWorkspaceService(
  options: CreateVersionWorkspaceServiceOptions,
): VersionWorkspaceService {
  const pageSize = options.pageSize ?? DEFAULT_COMMIT_RECORD_PAGE_SIZE;
  let recordsCache: CommitRecordsCache | null = null;
  let pendingRecordsRequest: PendingCommitRecordsRequest | null = null;

  /**
   * 读取并校验当前仓库上下文。
   *
   * @returns 可用于真实版本接口的仓库上下文。
   */
  function resolveRepositoryContextOrThrow(): VersionWorkspaceRepositoryContext {
    const context = normalizeRepositoryContext(options.getRepositoryContext());
    if (!context) {
      throw new Error('提交记录缺少仓库上下文，无法调用版本接口');
    }
    return context;
  }

  /**
   * 从后端加载提交记录。
   *
   * @param projectId 当前项目 ID。
   * @returns 提交记录列表。
   */
  async function loadCommitRecords(
    projectId: string,
    requestOptions: FetchProjectCommitRecordsOptions = {},
  ): Promise<readonly ProjectCommitRecord[]> {
    const context = normalizeRepositoryContext(options.getRepositoryContext());
    if (!context) {
      return [];
    }

    const cacheKey = createCommitRecordsContextKey(projectId, context);
    if (!requestOptions.forceRefresh && recordsCache?.key === cacheKey) {
      return recordsCache.records;
    }
    if (
      pendingRecordsRequest?.key === cacheKey
      && pendingRecordsRequest.signal === requestOptions.signal
      && !pendingRecordsRequest.signal?.aborted
    ) {
      return await pendingRecordsRequest.promise;
    }

    const pendingRequest: PendingCommitRecordsRequest = {
      key: cacheKey,
      signal: requestOptions.signal,
      promise: (async () => {
        const request = create(ListCommitsRequestSchema, {
          repositoryId: context.repositoryId,
          ...(context.branchId !== undefined ? { branchId: context.branchId } : {}),
          page: 1,
          limit: pageSize,
        });
        const response = requestOptions.signal
          ? await versionClient.listCommits(request, { signal: requestOptions.signal })
          : await versionClient.listCommits(request);
        const activeBranch = resolveActiveBranch(response.branches, context);
        const records = response.commits.map((commit) => (
          toProjectCommitRecord(commit, activeBranch, context)
        ));
        recordsCache = {
          key: cacheKey,
          records,
        };
        return records;
      })(),
    };
    pendingRecordsRequest = pendingRequest;

    try {
      return await pendingRequest.promise;
    } finally {
      if (pendingRecordsRequest === pendingRequest) {
        pendingRecordsRequest = null;
      }
    }
  }

  /**
   * 获取项目提交记录。
   *
   * @param projectId 项目 ID。
   * @returns 提交记录列表。
   */
  async function fetchProjectCommitRecords(
    projectId: string,
    requestOptions: FetchProjectCommitRecordsOptions = {},
  ): Promise<readonly ProjectCommitRecord[]> {
    return await loadCommitRecords(projectId, requestOptions);
  }

  /**
   * 获取历史提交快照。
   *
   * @param projectId 项目 ID。
   * @param commitId 提交 ID。
   * @returns 历史提交快照。
   */
  async function fetchCommitSnapshot(
    projectId: string,
    commitId: string,
    requestOptions: FetchProjectCommitSnapshotOptions = {},
  ): Promise<ProjectCommitSnapshot> {
    const context = resolveRepositoryContextOrThrow();
    const normalizedCommitId = commitId.trim();
    if (!normalizedCommitId) {
      throw new Error(`获取历史提交快照缺少提交 ID：projectId=${projectId}`);
    }

    const records = await loadCommitRecords(projectId, requestOptions);
    const record = records.find((item) => item.id === normalizedCommitId);
    const sourceBranchId = record?.branchId.trim() || context.branchId?.toString() || '';

    return {
      projectId,
      commitId: normalizedCommitId,
      title: record?.message ?? normalizedCommitId,
      branchName: record?.branchName ?? '默认分支',
      modelSource: {
        kind: 'project-root-snapshot',
        repositoryId: context.repositoryId.toString(),
        ...(sourceBranchId ? { branchId: sourceBranchId } : {}),
        commitId: normalizedCommitId,
      },
    };
  }

  /**
   * 基于历史提交创建可编辑工作区分支。
   *
   * @param projectId 项目 ID。
   * @param commitId 来源提交 ID。
   * @param branchName 用户输入的工作区分支名称。
   * @returns 创建结果。
   */
  async function createWorkspaceFromCommit(
    projectId: string,
    commitId: string,
    branchName: string,
  ): Promise<VersionWorkspaceCreationResult> {
    const context = resolveRepositoryContextOrThrow();
    const normalizedCommitId = commitId.trim();
    const normalizedBranchName = branchName.trim();

    if (!normalizedCommitId) {
      throw new Error('创建工作区分支缺少来源提交 ID');
    }
    if (!normalizedBranchName) {
      throw new Error('创建工作区分支缺少分支名称');
    }

    const request = create(CreateBranchRequestSchema, {
      repositoryId: context.repositoryId,
      branchName: normalizedBranchName,
      fromCommitId: normalizedCommitId,
      branchKind: BranchKind.WORKSPACE,
    });
    const branch = await versionClient.createBranch(request);
    if (branch.branchId <= 0n) {
      throw new Error(`CreateBranch 未返回有效工作区分支 ID：commitId=${normalizedCommitId}`);
    }

    recordsCache = null;
    pendingRecordsRequest = null;

    return {
      workspaceId: branch.branchId.toString(),
      branchId: branch.branchId.toString(),
      branchName: branch.branchName.trim() || normalizedBranchName,
      projectId,
      baseCommitId: normalizedCommitId,
      headCommitId: branch.headCommitId.trim() || normalizedCommitId,
    };
  }

  /**
   * 释放服务持有的提交记录缓存。
   */
  function dispose(): void {
    recordsCache = null;
    pendingRecordsRequest = null;
  }

  return {
    fetchProjectCommitRecords,
    fetchCommitSnapshot,
    createWorkspaceFromCommit,
    dispose,
  };
}
